import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  compareVersionDiffAsync,
  selectPlatform,
  selectVersionDiff,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import EmptyState from '../shared/EmptyState';
import MetricCard from '../shared/MetricCard';
import { versionDiffMetricColors, versionDiffStyles } from './versionDiffStyles';

export default function VersionDiffTab() {
  const dispatch = useDispatch();
  const versionDiff = useSelector(selectVersionDiff);
  const platform = useSelector(selectPlatform);
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(platform || 'android');

  const comparison = versionDiff.comparison;
  const riskSummary = comparison?.risk_summary || {};
  const diffSummary = comparison?.diff_summary || {};
  const items = Array.isArray(comparison?.items) ? comparison.items : [];

  const handleAnalyze = () => {
    if (!versionA || !versionB || versionA === versionB) {
      return;
    }

    dispatch(
      compareVersionDiffAsync({
        version_a: versionA.trim(),
        version_b: versionB.trim(),
        platform: selectedPlatform,
      }),
    );
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={versionDiffStyles.controlsPaper}>
        <Typography variant="subtitle2" fontWeight={800} sx={versionDiffStyles.title}>
          Version Diff
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={versionDiffStyles.description}>
          Compare page source elements between two app versions to predict which locators are likely
          to break.
        </Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Version A (baseline)"
              placeholder="e.g. 26.33.0 (Core6841)"
              value={versionA}
              onChange={(event) => setVersionA(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Version B (new)"
              placeholder="e.g. 26.34.0 (Core6945)"
              value={versionB}
              onChange={(event) => setVersionB(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Platform"
              value={selectedPlatform}
              onChange={(event) => setSelectedPlatform(event.target.value)}
            >
              <MenuItem value="android">Android</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              disabled={!versionA || !versionB || versionA === versionB || versionDiff.analyzing}
              onClick={handleAnalyze}
              sx={versionDiffStyles.analyzeButton}
            >
              {versionDiff.analyzing ? 'Analyzing...' : 'Analyze'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {versionDiff.error && <Alert severity="error">{versionDiff.error}</Alert>}

      {!comparison ? (
        <EmptyState>Select two versions to analyze predicted locator breakages.</EmptyState>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="High Risk"
                value={riskSummary.high || 0}
                color={versionDiffMetricColors.high}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Medium Risk"
                value={riskSummary.medium || 0}
                color={versionDiffMetricColors.medium}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Low Risk"
                value={riskSummary.low || 0}
                color={versionDiffMetricColors.low}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="New Elements"
                value={riskSummary.info || 0}
                color={versionDiffMetricColors.info}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={versionDiffStyles.catalogPaper}>
            <Typography variant="body2" color="text.secondary">
              Catalog A: {comparison.catalog_a_size || 0} elements ({comparison.xml_files_a || 0}{' '}
              XMLs) | Catalog B: {comparison.catalog_b_size || 0} elements (
              {comparison.xml_files_b || 0} XMLs) | Removed: {diffSummary.removed || 0} | New:{' '}
              {diffSummary.new || 0} | Changed: {diffSummary.changed || 0} | Unchanged:{' '}
              {diffSummary.unchanged || 0}
            </Typography>
          </Paper>

          {items.length === 0 ? (
            <EmptyState>
              No risks detected. Run more scenarios on both versions to build a larger element
              catalog.
            </EmptyState>
          ) : (
            <Paper variant="outlined" sx={versionDiffStyles.tablePaper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Risk</TableCell>
                    <TableCell>Element</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Used In</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={`${item.element_id || 'item'}-${index}`} hover>
                      <TableCell sx={versionDiffStyles.riskCell(item.risk)}>{item.risk}</TableCell>
                      <TableCell sx={versionDiffStyles.elementCell} title={item.element_id}>
                        {item.element_id}
                      </TableCell>
                      <TableCell sx={versionDiffStyles.reasonCell} title={item.reason}>
                        {item.reason}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(item.used_in) && item.used_in.length > 0
                          ? item.used_in.join(', ')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Stack>
      )}
    </Stack>
  );
}
